import { EntityType } from '@frontend/common';
import { DefaultBadge } from '@frontend/components/badge';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { ProductCategoryClient } from '@frontend/group/api';
import { IGroup } from '@frontend/group/types';
import { CommonMessage, getCorrectTranslation, useLocale } from '@frontend/lang';
import { ModalType } from '@frontend/modal';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { usePlatformModalHost } from '../../../modal/platform-modal-host/platform-modal-host.context';
import OverviewHeader from '../../header/overview-header';
import { TabFilter } from '../../tab-filter/tab-filter.component';
import { useCategories } from './categories.controller';
import { CreateCategoryModal } from './create-category-modal/create-category-modal.component';
import { DirectoryView } from './directory-view/directory-view.component';

export const Categories = () => {
    const viewProps = useCategories();
    const { locale } = useLocale();
    const { openPlatformDetail } = usePlatformModalHost();

    const content = useMemo(() => {
        switch (viewProps.activeTab) {
            case 'directory':
                return (
                    <DirectoryView
                        navigate={viewProps.navigate}
                        location={viewProps.location}
                        dispatch={viewProps.dispatch}
                        listId={viewProps.getListId('directory')}
                    />
                );
            case 'table':
                return (
                    <ReactiveObjectList<IGroup>
                        id={viewProps.getListId('table')}
                        useStaticListId
                        fetch={(arg) => ProductCategoryClient.fetchProductCategories(arg)}
                        dispatch={viewProps.dispatch}
                        type={EntityType.PRODUCT_CATEGORY}
                        eventListener={EntityType.GROUP}
                        onSelectElement={(obj) => openPlatformDetail({ entityType: EntityType.PRODUCT_CATEGORY, id: obj.id })}
                        columns={{
                            columns: ['name', 'num_entities'],
                            customCells: new Map([
                                [
                                    'name',
                                    (obj) => (
                                        <td className={defaultCellClassName}>
                                            <p className='text-foreground-default truncate'>{getCorrectTranslation(locale, obj.row.original, 'name')}</p>
                                        </td>
                                    )
                                ],
                                [
                                    'num_entities',
                                    (obj) => (
                                        <td className={defaultCellClassName}>
                                            {obj.row.original.num_entities !== null && obj.row.original.num_entities !== undefined && (
                                                <DefaultBadge
                                                    label={obj.row.original.num_entities}
                                                    styles={{
                                                        container: {
                                                            color: 'bg-background-surface'
                                                        },
                                                        label: 'text-foreground-default'
                                                    }}
                                                />
                                            )}
                                        </td>
                                    )
                                ]
                            ]),
                            headerOverwrite: new Map([
                                ['name', CommonMessage.OBJECTS.CATEGORY.CATEGORY_NAME],
                                [
                                    'num_entities',
                                    <FormattedMessage
                                        id='attribute_detail_modal.linked_products'
                                        defaultMessage='Linked products'
                                        key='attribute_detail_modal.linked_products'
                                    />
                                ]
                            ])
                        }}
                        activeFilter={{
                            searchKeys: ['search'],
                            displayName: new Map([['search', CommonMessage.OBJECTS.CATEGORY.CATEGORY_NAME]])
                        }}
                    />
                );
            default:
                return null;
        }
    }, [viewProps.activeTab, viewProps, locale]);

    return (
        <div className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'>
            <OverviewHeader
                title={
                    <div className='flex gap-2'>
                        <span>{CommonMessage.OBJECTS.CATALOG.DEFAULT}</span>
                        <span>/</span>
                        <span>{CommonMessage.OBJECTS.CATEGORY.PLURAL}</span>
                    </div>
                }
                create={{
                    onCreate: () =>
                        viewProps.openModal(viewProps.categoryModalId, () => <CreateCategoryModal />, {}, { className: 'w-[600px]', type: ModalType.PANE }),
                    entity: EntityType.CATEGORY
                }}
            />
            <div className='inline-flex flex-col items-start justify-start gap-4 self-stretch'>
                <TabFilter
                    keys={['directory', 'table']}
                    displayMap={
                        new Map([
                            [
                                'directory',
                                <FormattedMessage
                                    key='categories.directory-view'
                                    id='categories.directory-view'
                                    defaultMessage='Directory view'
                                />
                            ],
                            [
                                'table',
                                <FormattedMessage
                                    key='categories.table-view'
                                    id='categories.table-view'
                                    defaultMessage='Table view'
                                />
                            ]
                        ])
                    }
                    defaultActive='directory'
                    useQueryParams={{ key: 'tab' }}
                    filterKeys={['search', 'All']}
                />
                {content}
            </div>
        </div>
    );
};
